/**
 * Small i18n helpers shared by the admin, compliance and connector panels.
 *
 * Every call passes the English text as the default, so the panels read right
 * when i18n isn't initialised (unit tests, the admin portal's empty bundle).
 */
import type { ReactNode } from 'react';
import i18next, { type TFunction } from 'i18next';
import { fill, tv, type Vars } from '../../lib/i18nText';

export { tv, type Vars } from '../../lib/i18nText';

/** Translation outside React components (module-level helpers): uses the global i18next instance. */
export function gt(key: string, fallback: string, vars: Vars = {}): string {
  const s = i18next.isInitialized ? i18next.t(key, { defaultValue: fallback, ...vars }) : fallback;
  return fill(typeof s === 'string' && s ? s : fallback, vars);
}

/** The app language for toLocale*()/Intl, or undefined (browser default) when i18n isn't running. */
export function uiLang(i18n: { language?: string } | undefined): string | undefined {
  return i18n?.language || undefined;
}

/**
 * Renders a translated string that marks emphasis with <b>…</b> and code with <code>…</code>,
 * so translators can move the emphasised words. React escapes the text; no HTML is injected.
 */
export function rich(s: string, cls: { b?: string; code?: string } = {}): ReactNode[] {
  return s.split(/(<b>.*?<\/b>|<code>.*?<\/code>)/g).filter(Boolean).map((part, i) => {
    const b = /^<b>(.*)<\/b>$/.exec(part);
    if (b) return <strong key={i} className={cls.b}>{b[1]}</strong>;
    const c = /^<code>(.*)<\/code>$/.exec(part);
    if (c) return <code key={i} className={cls.code}>{c[1]}</code>;
    return part;
  });
}

/** "Just now" / "5m ago" / "3h ago" / "2d ago" for a past timestamp; "Never" when empty. */
export function relTime(t: TFunction, iso: string | null | undefined): string {
  if (!iso) return t('adminUi.time.never', 'Never');
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return t('adminUi.time.justNow', 'Just now');
  if (diff < 3_600_000) return tv(t, 'adminUi.time.minutesAgo', '{{n}}m ago', { n: Math.floor(diff / 60_000) });
  if (diff < 86_400_000) return tv(t, 'adminUi.time.hoursAgo', '{{n}}h ago', { n: Math.floor(diff / 3_600_000) });
  return tv(t, 'adminUi.time.daysAgo', '{{n}}d ago', { n: Math.floor(diff / 86_400_000) });
}
